import React from "react";

// P0 outage fix (2026-02-20) — hiring-manager blank-screen on Mas Callnet.
// Any UI crash must (a) show a graceful fallback, and (b) be REPORTED to
// the backend so it surfaces on /bmi-ops. A boundary that only paints a
// friendly message means the next occurrence is silent again — which is
// exactly how the P0 reached a real client without us knowing.

const REPORTER_URL = `${process.env.REACT_APP_BACKEND_URL || ""}/api/ops/ui-errors`;

/** Fire-and-forget POST — best-effort, never rejects. Safe to call from
 *  both boundaries (React errors) and caught fetch failures. */
export function reportUiError(payload) {
    try {
        const u = (typeof window !== "undefined" && window.__BMI_USER__) || {};
        const body = JSON.stringify({
            origin: "error_boundary",
            route: (typeof window !== "undefined")
                ? window.location.pathname + window.location.search : null,
            app_version: process.env.REACT_APP_VERSION || null,
            user_role: u.role || null,
            team_role: u.team_role || null,
            user_id: u.user_id || null,
            user_email: u.team_email || u.email || null,
            company_name: u.company_name || null,
            ...payload,
        });
        if (typeof navigator !== "undefined" && navigator.sendBeacon) {
            const blob = new Blob([body], { type: "application/json" });
            navigator.sendBeacon(REPORTER_URL, blob);
            return;
        }
        fetch(REPORTER_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            keepalive: true,
            credentials: "include",
        }).catch(() => {});
    } catch { /* never propagate reporter failures */ }
}

/**
 * Top-level error boundary.
 *
 * Prior to this component, any React render error (e.g. calling `.includes`
 * on `null`, an unexpected server payload, a crashing child) would blow up
 * the entire tree — leaving users staring at a blank white page with no way
 * to recover except a hard refresh. Users cannot report the failure because
 * they see nothing.
 *
 * This boundary catches the error, keeps the page painted, and gives the
 * user a friendly retry option plus a copyable stack trace they can send to
 * support. It also logs to the browser console so we have breadcrumbs when
 * debugging in production — AND persists the crash to /api/ops/ui-errors so
 * the /bmi-ops UI-errors tab surfaces it without waiting for a customer
 * complaint. Wrap sub-trees with `<ErrorBoundary name="pipeline">…` to
 * scope the fallback + carry a `context.boundary` label in the report.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, info: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        console.error("[ErrorBoundary] Uncaught render error:", error, info);
        this.setState({ info });
        reportUiError({
            message: (error && (error.message || String(error))) || "unknown",
            stack: error?.stack || null,
            component_stack: info?.componentStack || null,
            context: { boundary: this.props.name || "root" },
        });
    }

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = "/dashboard";
    };

    render() {
        if (!this.state.error) return this.props.children;

        const stack = (this.state.error && this.state.error.stack) || String(this.state.error);
        const componentStack = (this.state.info && this.state.info.componentStack) || "";

        return (
            <div
                data-testid={this.props.name ? `error-boundary-${this.props.name}` : "app-error-boundary"}
                role="alert"
                className="min-h-screen w-full bg-background text-foreground flex items-center justify-center p-6"
            >
                <div className="max-w-lg w-full border border-border rounded-sm bg-card p-8 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-2">
                        Something broke
                    </div>
                    <h1 className="font-serif text-3xl leading-tight mb-3">
                        This page hit an unexpected error.
                    </h1>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                        You&apos;re not stuck — reload the page or head back to your dashboard. If
                        this keeps happening, please share the details below with support so we
                        can fix it fast.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2 mb-6">
                        <button
                            data-testid="error-boundary-reload"
                            onClick={this.handleReload}
                            className="flex-1 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-medium text-sm py-2.5 rounded-sm hover:-translate-y-[1px] hover:shadow-md transition-all"
                        >
                            Reload page
                        </button>
                        <button
                            data-testid="error-boundary-home"
                            onClick={this.handleGoHome}
                            className="flex-1 inline-flex items-center justify-center gap-2 border border-border bg-background hover:bg-muted text-sm py-2.5 rounded-sm transition-all"
                        >
                            Go to dashboard
                        </button>
                    </div>
                    <details className="text-xs text-muted-foreground">
                        <summary className="cursor-pointer uppercase tracking-[0.22em] text-[10px] mb-2">
                            Error details (for support)
                        </summary>
                        <pre
                            data-testid="error-boundary-stack"
                            className="mt-2 p-3 bg-muted/60 rounded-sm text-[11px] leading-relaxed whitespace-pre-wrap break-words max-h-80 overflow-auto"
                        >
{stack}
{componentStack ? "\n\nComponent stack:" + componentStack : ""}
                        </pre>
                    </details>
                </div>
            </div>
        );
    }
}
